import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const AuthContext = createContext(null);

const BACKEND_URL =
  window.location.hostname.endsWith("preview.emergentagent.com")
    ? window.location.origin
    : "https://api.recoverymatch.com";

const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      
      // Get error details for better handling
      const errorMessage = error.response?.data?.detail || "";
      const statusCode = error.response?.status;

      // Only clear the session on real auth failures (401/403).
      // Transient errors (429 rate limit, network, 5xx) keep the session.
      if (statusCode !== 401 && statusCode !== 403) {
        setLoading(false);
        return;
      }

      // Clear invalid token
      localStorage.removeItem("token");
      localStorage.removeItem("recoverymatch_admin_token");
      setToken(null);
      setUser(null);
      
      // Show user-friendly message based on error type
      if (statusCode === 401) {
        if (errorMessage.includes("User not found")) {
          toast.error("Your session is invalid. Please log in again.", {
            description: "Your account may have been updated.",
            duration: 5000
          });
        } else if (errorMessage.includes("expired") || errorMessage.includes("Invalid token")) {
          toast.error("Your session has expired. Please log in again.", {
            duration: 5000
          });
        } else {
          toast.error("Please log in to continue.", {
            duration: 3000
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { 
      name, 
      email, 
      password,
      signup_platform: 'web'
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUser,
    setUser,
    refreshUser: fetchUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
